import React, { useMemo } from "react";

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function fmt(v, digits = 2) {
  const n = num(v);
  return n == null ? "—" : n.toLocaleString(undefined, { maximumFractionDigits: digits, minimumFractionDigits: digits });
}

function signed(v, digits = 2) {
  const n = num(v);
  if (n == null) return "—";
  return `${n > 0 ? "+" : ""}${fmt(n, digits)}`;
}

function ewma(history, span = 8) {
  if (!history.length) return null;
  const alpha = 2 / (span + 1);
  let value = history[0];
  for (let i = 1; i < history.length; i += 1) value = alpha * history[i] + (1 - alpha) * value;
  return value;
}

function median(values) {
  const a = values.map(num).filter(v => v != null).sort((x, y) => x - y);
  if (!a.length) return null;
  const m = Math.floor(a.length / 2);
  return a.length % 2 ? a[m] : (a[m - 1] + a[m]) / 2;
}

function Table({ columns, rows, C, MONO }) {
  return (
    <div style={{ overflowX: "auto", overflowY: "auto", maxHeight: 390, border: `1px solid ${C.border}`, borderRadius: 10, background: C.panel }}>
      <table style={{ width: "100%", minWidth: 900, borderCollapse: "separate", borderSpacing: 0, fontFamily: MONO, fontSize: 10 }}>
        <thead>
          <tr>
            {columns.map(col => (
              <th key={col.key} style={{ position: "sticky", top: 0, zIndex: 2, background: C.panel, color: C.muted, textAlign: col.align || "left", padding: "10px", borderBottom: `1px solid ${C.border2}`, whiteSpace: "nowrap", fontWeight: 700 }}>
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row._key || i}>
              {columns.map(col => (
                <td key={col.key} style={{ color: col.color ? col.color(row, C) : C.text, textAlign: col.align || "left", padding: "9px 10px", borderBottom: `1px solid ${C.border}`, whiteSpace: col.wrap ? "normal" : "nowrap", lineHeight: 1.45 }}>
                  {col.render ? col.render(row, C) : (row[col.key] ?? "—")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function buildRows(method, data) {
  return method === "GASLIFT" ? (data?.timeline || []) : (data?.per_cycle || []);
}

function buildModel(method, data) {
  const source = buildRows(method, data);
  const rows = source.map((r, i) => ({ ...r, _index: i }));
  const rateKey = method === "GASLIFT" ? "InjectionGasRate_MCFD" : "AnnulusGasRate_MCFD";
  const histories = {};

  // One-step baseline: only observations before the current row from the same well are used.
  const enriched = rows.map((r, i) => {
    const well = r.Well_ID || "well";
    if (!histories[well]) histories[well] = [];
    const rate = num(r[rateKey]);
    const predicted = ewma(histories[well]);
    if (rate != null) histories[well].push(rate);
    return { ...r, _key: `${well}-${i}`, _predicted_rate: predicted, _well_depth_ft: num(data?.well_depth_ft?.[well]) };
  });

  const groups = {};
  enriched.forEach(r => {
    const well = r.Well_ID || "well";
    if (!groups[well]) groups[well] = [];
    groups[well].push(r);
  });

  const recommendations = {};
  const recommendationMeta = {};

  Object.entries(groups).forEach(([well, g]) => {
    const loadFactor = r => {
      const pc = num(r.CsgAtOpen);
      const pt = num(r.TbgAtOpen);
      const pl = num(r.LineAtOpen);
      if (pc == null || pt == null || pl == null || Math.abs(pc - pl) < 1e-9) return null;
      return (pc - pt) / (pc - pl);
    };

    if (method === "GAPL" || method === "PAGL") {
      // Engineering envelope: plunger rise 500–1000 ft/min, with ~750 ft/min as the
      // preferred target; load factor < 0.5 is the conventional opening criterion.
      // Injection pressure must exceed casing pressure for gas to enter the annulus.
      let candidates = g.filter(r => {
        const v = num(r.ArrivalVelocity_ftmin);
        const injP = num(r.InjectionPressure_psi);
        const csgP = num(r.CsgAtOpen);
        const lf = loadFactor(r);
        const common = v != null && v >= 500 && v <= 1000 && injP != null && csgP != null && injP > csgP && lf != null && lf < 0.5;
        return common && (method === "GAPL" ? r.PlungerArrived === true : r.PlungerAssistEffective === true);
      });

      // If the strict outcome filter is too restrictive, relax only the outcome flag;
      // keep the physical envelope intact. This avoids inventing a rate.
      if (!candidates.length) {
        candidates = g.filter(r => {
          const v = num(r.ArrivalVelocity_ftmin);
          const injP = num(r.InjectionPressure_psi);
          const csgP = num(r.CsgAtOpen);
          const lf = loadFactor(r);
          return v != null && v >= 500 && v <= 1000 && injP != null && csgP != null && injP > csgP && lf != null && lf < 0.5;
        });
      }

      // The objective is to lift liquid effectively without selecting an arbitrary/random rate.
      // Use the highest-liquid-production 20% of physically feasible historical cycles and
      // take their median injection rate for a stable, data-derived set-point.
      const pool = candidates.length ? candidates : g.filter(r => num(r[rateKey]) != null);
      const ranked = pool.slice().sort((a, b) => {
        const la = num(a.LiquidProductionRate_bbl) ?? -Infinity;
        const lb = num(b.LiquidProductionRate_bbl) ?? -Infinity;
        if (lb !== la) return lb - la;
        return (num(a[rateKey]) ?? Infinity) - (num(b[rateKey]) ?? Infinity);
      });
      const topN = Math.max(1, Math.ceil(ranked.length * 0.2));
      const selected = ranked.slice(0, topN);
      const recommended = median(selected.map(r => r[rateKey]));
      recommendations[well] = recommended;
      recommendationMeta[well] = {
        selected,
        targetVelocity: 750,
        minVelocity: 500,
        maxVelocity: 1000,
        minLoadFactor: 0.5,
      };
    } else {
      // Gas-lift optimum: maximize observed liquid production subject to the
      // unloading requirement. Use the median critical rate for the well and a
      // 100-MCFD rate bin so one noisy point cannot become the recommendation.
      const valid = g
        .map(r => ({ r, rate: num(r.InjectionGasRate_MCFD), critical: num(r.CriticalUnloadingRate_MCFD), liquid: num(r.LiquidProductionRate_bbld) }))
        .filter(x => x.rate != null && x.liquid != null && x.critical != null);
      const critical = median(valid.map(x => x.critical));
      const feasible = valid.filter(x => critical == null || x.rate >= critical);
      const pool = feasible.length ? feasible : valid;
      let best = null;
      if (pool.length) {
        const bins = {};
        pool.forEach(x => {
          const key = Math.floor(x.rate / 100) * 100;
          if (!bins[key]) bins[key] = [];
          bins[key].push(x);
        });
        const candidates = Object.entries(bins).map(([bin, items]) => ({
          bin: Number(bin),
          rate: median(items.map(x => x.rate)),
          liquid: median(items.map(x => x.liquid)),
          count: items.length,
          items,
        })).sort((a, b) => b.liquid - a.liquid || b.count - a.count || a.rate - b.rate);
        best = candidates[0];
      }
      recommendations[well] = best ? best.rate : critical;
      recommendationMeta[well] = { selected: best ? best.items.map(x => x.r) : [], criticalRate: critical, binWidth: 100 };
    }
  });

  // The backend is the source of truth for engineering screening, the selected
  // recommendation, and the cycle-specific evidence. Keep the local calculation
  // only as a compatibility fallback if an older backend is running.
  const backendOpt = data?.optimization || null;
  if (backendOpt?.recommendations) {
    Object.entries(backendOpt.recommendations).forEach(([well, rate]) => {
      recommendations[well] = num(rate);
    });
  }

  function recommendationBasis(r, recommended) {
    const cycleKey = method === "GASLIFT"
      ? (r.Timestamp || "")
      : (r.CycleSerialNumber == null || r.CycleSerialNumber === ""
          ? ""
          : (Number.isFinite(Number(r.CycleSerialNumber)) ? String(Math.trunc(Number(r.CycleSerialNumber))) : String(r.CycleSerialNumber)));
    const basisKey = `${r.Well_ID || "well"}|${cycleKey}`;
    const backendBasis = backendOpt?.basis_by_cycle?.[basisKey];
    if (backendBasis) return backendBasis;

    if (recommended == null) return "No valid engineering-constrained historical data was available for this well.";

    if (method === "GAPL" || method === "PAGL") {
      const velocity = num(r.ArrivalVelocity_ftmin);
      const lf = (() => {
        const pc = num(r.CsgAtOpen), pt = num(r.TbgAtOpen), pl = num(r.LineAtOpen);
        return pc != null && pt != null && pl != null && Math.abs(pc - pl) > 1e-9 ? (pc - pt) / (pc - pl) : null;
      })();
      const glr = num(r.GLR_scfbbl);
      const depth = num(r._well_depth_ft);
      const minGlr = depth != null ? 400 * (depth / 1000) : null;
      const velocityText = velocity == null ? "arrival velocity unavailable" : `${fmt(velocity, 0)} ft/min vs 500–1,000 ft/min feasible range and ~750 ft/min target`;
      const lfText = lf == null ? "load factor unavailable" : `load factor ${fmt(lf, 2)} vs <0.50 criterion`;
      const glrText = minGlr == null || glr == null ? "" : `; GLR ${fmt(glr, 0)} scf/bbl vs ${fmt(minGlr, 0)} scf/bbl minimum depth-based requirement`;
      return `${velocityText}; ${lfText}${glrText}. ${fmt(recommended)} MCFD is the median injection rate from the highest-liquid-production 20% of qualifying historical cycles${method === "PAGL" ? " with effective plunger assistance" : " with successful plunger arrival"}.`;
    }

    const critical = num(r.CriticalUnloadingRate_MCFD);
    const actual = num(r.InjectionGasRate_MCFD);
    const liquid = num(r.LiquidProductionRate_bbld);
    return `Observed performance-curve optimum: ${fmt(recommended)} MCFD, selected from rates at/above the well's critical unloading rate${critical != null ? ` (${fmt(critical)} MCFD for this record)` : ""}; actual ${fmt(actual)} MCFD produced ${fmt(liquid)} bbl/d. The rate is binned to 100 MCFD to avoid choosing a single noisy observation.`;
  }

  const cycleRows = enriched.map(r => {
    const well = r.Well_ID || "well";
    const actual = num(r[rateKey]);
    const recommended = recommendations[well];
    const meta = recommendationMeta[well] || {};
    const depth = meta.selected?.[0]?._well_depth_ft;
    return {
      ...r,
      _well_depth_ft: depth ?? r._well_depth_ft,
      _recommended_rate: recommended,
      _difference: actual != null && recommended != null ? recommended - actual : null,
      _basis: recommendationBasis({ ...r, _well_depth_ft: depth ?? r._well_depth_ft }, recommended),
    };
  });

  const factorDefs = method === "GAPL" ? [
    ["Injection Rate", rateKey, "MCFD"],
    ["Gas Injection Volume", "GasInjectionVolume_MCF", "MCF"],
    ["Injection Pressure", "InjectionPressure_psi", "psi"],
    ["Injection Duration", "InjectionDuration_min", "min"],
    ["Arrival Velocity", "ArrivalVelocity_ftmin", "ft/min"],
    ["Liquid Production", "LiquidProductionRate_bbl", "bbl/cycle"],
    ["GLR", "GLR_scfbbl", "scf/bbl"],
  ] : method === "PAGL" ? [
    ["Injection Rate", rateKey, "MCFD"],
    ["Gas Injection Volume", "GasInjectionVolume_MCF", "MCF"],
    ["Injection Pressure", "InjectionPressure_psi", "psi"],
    ["Injection Duration", "InjectionDuration_min", "min"],
    ["Arrival Velocity", "ArrivalVelocity_ftmin", "ft/min"],
    ["Liquid Production", "LiquidProductionRate_bbl", "bbl/cycle"],
    ["Plunger Assist Effective", "PlungerAssistEffective", "boolean"],
  ] : [
    ["Injection Gas Rate", rateKey, "MCFD"],
    ["Critical Unloading Rate", "CriticalUnloadingRate_MCFD", "MCFD"],
    ["Casing Pressure", "CasingPressure_psi", "psi"],
    ["Tubing Pressure", "TubingPressure_psi", "psi"],
    ["Liquid Production", "LiquidProductionRate_bbld", "bbl/d"],
    ["GLR", "GLR_scfbbl", "scf/bbl"],
  ];

  const factorRows = [];
  Object.entries(groups).forEach(([well, g]) => {
    const meta = recommendationMeta[well] || {};
    const selected = meta.selected || [];
    const topMedian = key => median(selected.map(r => r[key]));
    const wellDepth = num(data?.well_depth_ft?.[well]);
    const rateRec = recommendations[well] ?? null;
    const durationRec = topMedian("InjectionDuration_min");
    const pressureRec = topMedian("InjectionPressure_psi");
    const liquidRec = topMedian(method === "GASLIFT" ? "LiquidProductionRate_bbld" : "LiquidProductionRate_bbl");
    const glrMin = method !== "GASLIFT" && wellDepth != null ? 400 * (wellDepth / 1000) : null;
    const gasVolRec = durationRec != null && rateRec != null ? rateRec * durationRec / 1440 : null;
    const criticalRec = method === "GASLIFT" ? (meta.criticalRate ?? median(g.map(r => r.CriticalUnloadingRate_MCFD))) : null;
    const gasliftPressureRec = method === "GASLIFT" ? topMedian("CasingPressure_psi") : null;
    const gasliftTubingRec = method === "GASLIFT" ? topMedian("TubingPressure_psi") : null;

    factorDefs.forEach(([label, key, unit]) => {
      const values = g.map(r => num(r[key])).filter(v => v != null);
      const actual = values.length ? values[values.length - 1] : null;
      const predicted = values.length > 1 ? ewma(values.slice(0, -1)) : null;
      const backendFactors = backendOpt?.factor_recommendations?.[well] || {};
      let recommended = Object.prototype.hasOwnProperty.call(backendFactors, label)
        ? backendFactors[label]
        : rateRec;
      if (method !== "GASLIFT" && !Object.prototype.hasOwnProperty.call(backendFactors, label)) {
        if (label === "Gas Injection Volume") recommended = gasVolRec;
        else if (label === "Injection Pressure") recommended = pressureRec;
        else if (label === "Injection Duration") recommended = durationRec;
        else if (label === "Arrival Velocity") recommended = 750;
        else if (label === "Liquid Production") recommended = liquidRec;
        else if (label === "GLR") recommended = glrMin;
        else if (label === "Plunger Assist Effective") recommended = "Effective";
      } else if (!Object.prototype.hasOwnProperty.call(backendFactors, label)) {
        if (label === "Critical Unloading Rate") recommended = criticalRec;
        else if (label === "Casing Pressure") recommended = gasliftPressureRec;
        else if (label === "Tubing Pressure") recommended = gasliftTubingRec;
        else if (label === "Liquid Production") recommended = liquidRec;
        else if (label === "GLR") recommended = null;
      }
      factorRows.push({
        _key: `${well}-${label}`,
        well,
        factor: label,
        predicted,
        actual,
        difference: predicted != null && actual != null ? actual - predicted : null,
        recommended,
        unit,
      });
    });
  });

  return { cycleRows, factorRows };
}
export default function InjectionOptimizationPanel({ method, data, C, MONO, SANS, selectedWell = "all" }) {
  const model = useMemo(() => buildModel(method, data), [method, data]);
  if (!model.cycleRows.length) return null;

  const rateUnit = "MCFD";
  const scopedCycleRows = model.cycleRows.filter(r => !selectedWell || selectedWell === "all" || r.Well_ID === selectedWell);
  const scopedFactorRows = model.factorRows.filter(r => !selectedWell || selectedWell === "all" || r.well === selectedWell);
  const title = method === "GASLIFT" ? "Gas Lift Injection Rate Recommendation" : `${method} Injection Rate Recommendation`;
  const deltaColor = (r) => {
    const d = num(r._difference);
    if (d == null) return C.muted;
    return Math.abs(d) < 0.01 ? C.green : d > 0 ? C.amber : C.cyan;
  };

  return (
    <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 18 }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "20px 16px", position: "relative" }}>
        <div style={{ position: "absolute", top: 0, left: 20, right: 20, height: 2, background: `linear-gradient(to right, transparent, ${C.green}88, transparent)` }} />
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontFamily: SANS, fontWeight: 700, fontSize: 13, color: C.text }}>
            {method === "GASLIFT" ? "Time-Based Injection Rate Recommendation" : "Cycle-Wise Injection Rate Recommendation"}
          </div>
          <div style={{ fontFamily: MONO, fontSize: 10, color: C.muted, marginTop: 4 }}>
            {method === "GASLIFT"
              ? "Actual injection rate, expected rate, and recommended rate for each time point."
              : "Measured injection rate vs data-derived prediction and recommended/optimal rate. Difference shows how far the current cycle is from the recommendation."}
          </div>
        </div>
        <Table
          C={C} MONO={MONO} rows={scopedCycleRows}
          columns={[
            { key: "cycle", label: method === "GASLIFT" ? "Timestamp" : "Cycle", render: r => method === "GASLIFT" ? (r.Timestamp || "—") : (r.CycleSerialNumber ?? "—") },
            { key: "actual", label: "Actual Injection Rate", align: "right", render: r => `${fmt(r[method === "GASLIFT" ? "InjectionGasRate_MCFD" : "AnnulusGasRate_MCFD"])} ${rateUnit}` },
            { key: "predicted", label: "Predicted Rate", align: "right", render: r => `${fmt(r._predicted_rate)} ${rateUnit}` },
            { key: "recommended", label: "Recommended / Optimal", align: "right", render: r => `${fmt(r._recommended_rate)} ${rateUnit}` },
            { key: "difference", label: "Difference", align: "right", render: r => signed(r._difference), color: deltaColor },
            { key: "basis", label: "Recommendation Basis", wrap: true, render: r => r._basis || "—" },
          ]}
        />
      </div>

      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "20px 16px" }}>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontFamily: SANS, fontWeight: 700, fontSize: 13, color: C.text }}>Predicted vs Actual Operating Factors</div>
          <div style={{ fontFamily: MONO, fontSize: 10, color: C.muted, marginTop: 4 }}>
            Predicted values use earlier observations from the same well; actual values are taken directly from the displayed data. No random or hardcoded operating values are used.
          </div>
        </div>
        <Table
          C={C} MONO={MONO} rows={scopedFactorRows}
          columns={[
            { key: "factor", label: "Operating Factor" },
            { key: "predicted", label: "Predicted", align: "right", render: r => fmt(r.predicted) },
            { key: "actual", label: "Actual", align: "right", render: r => fmt(r.actual) },
            { key: "recommended", label: "Recommended / Optimal", align: "right", render: r => typeof r.recommended === "string" ? r.recommended : `${fmt(r.recommended)}${r.unit ? ` ${r.unit}` : ""}` },
            { key: "difference", label: "Difference", align: "right", render: r => signed(r.difference) },
            { key: "unit", label: "Unit" },
          ]}
        />
      </div>
    </div>
  );
}
